import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { getShrimpMortalityKey } from 'common/constants/queryKeys'
import useCycleApi from 'common/hooks/useCycleApi'
import { BasePaginationResponseV2, BaseResponse } from 'common/types/api'
import { formatDate } from 'common/utils/date'
import { useGetPondNameById } from 'modules/pond/api/selector'
import { selectedCycleIdState } from 'modules/pond/stores/atom'
import { DisplayMode } from 'modules/water/api/types'
import { useParams } from 'react-router'
import { useRecoilValue } from 'recoil'

import { DEATH_REQUEST_CONFIG, getShrimpDiseases } from '.'
import { deadShrimpDateFilterState, deadShrimpDateTableFilterState } from '../stores/atom'
import { DeadRecentSize, DeadSummaryToday, Death, DeathChart } from './types'

const ROWS_PER_PAGE = 10

export const useGetDeadShrimps = (mode: DisplayMode) => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)

  const dateFilter = useRecoilValue(deadShrimpDateFilterState)
  const dateRangeFilter = useRecoilValue(deadShrimpDateTableFilterState)

  const initParams = {
    params: {
      pageSize: ROWS_PER_PAGE,
      page: 1,

      ...(mode === 'card'
        ? { date: dateFilter ? formatDate(dateFilter) : undefined }
        : {
            startDate: dateRangeFilter?.setDate ? formatDate(dateRangeFilter.setDate) : undefined,
            endDate: dateRangeFilter?.endDate ? formatDate(dateRangeFilter.endDate) : undefined,
          }),
    },
    cycleId,
  }

  return useInfiniteQuery({
    queryKey: getShrimpMortalityKey.list(cycleId || '', initParams.params),
    queryFn: async ({ pageParam = initParams }) => {
      const { data } = await cycleApi.request<BasePaginationResponseV2<Death>>({
        ...DEATH_REQUEST_CONFIG.get,
        ...pageParam,
      })

      return {
        shrimps: data.data,
        nextPage: data.nextPage,
      }
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.nextPage) return undefined

      return {
        cycleId: initParams.cycleId,
        params: {
          ...initParams.params,
          page: lastPage.nextPage,
        },
      }
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}

export const useGetDeadShrimpLineChart = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)

  return useQuery({
    queryKey: getShrimpMortalityKey.chart(cycleId || ''),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<DeathChart[]>>(
        DEATH_REQUEST_CONFIG.getLineChart
      )
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}

export const useGetShrimpDiseases = () => {
  const { pondId } = useParams()
  const { data: pond } = useGetPondNameById(pondId ?? '')

  return useQuery({
    queryKey: getShrimpMortalityKey.disease(pond?.species.id || 0),
    queryFn: async () => {
      const { data } = await getShrimpDiseases(pond?.species.id || 0)
      return data.data
    },
    staleTime: Infinity,
    enabled: !!pond?.species.id,
  })
}

export const useGetSummaryToday = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)
  return useQuery({
    queryKey: getShrimpMortalityKey.summaryToday(cycleId || ''),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<DeadSummaryToday>>({
        ...DEATH_REQUEST_CONFIG.getSummaryToday,
      })
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}

export const useGetRecentSize = (date?: string) => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)
  return useQuery({
    queryKey: getShrimpMortalityKey.recentSize(cycleId || '', date),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<DeadRecentSize>>({
        ...DEATH_REQUEST_CONFIG.getRecentSize,
        params: { date },
      })
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}
